import { useState, useEffect, useMemo } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import { useTheme } from '@/contexts/ThemeContext'
import { dashboardApi } from '@/api'
import {
  HomeIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BoltIcon,
  StarIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  CubeIcon,
  ChevronDownIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { HelpButton } from '@/components/help'

// Tipo per le voci di navigazione con permesso SINGOLO
// I permessi sono DINAMICI e vengono dal database - NON hardcoded per ruolo!
interface NavItem {
  name: string
  href?: string             // Optional se ha children (menu collapsibile)
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  permission?: string       // Permesso SINGOLO richiesto (dal database)
  superAdminOnly?: boolean  // Solo super_admin
  children?: NavItem[]      // Sotto-voci per menu collapsibili
  exactMatch?: boolean      // Se true, usa match esatto invece di startsWith
}

// Navigazione principale - accessibile a tutti gli utenti autenticati
// Ogni voce richiede il permesso .view del relativo modulo
const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Forum', href: '/forum', icon: ChatBubbleLeftRightIcon, permission: 'forum.view' },
  { name: 'Corsi', href: '/courses', icon: AcademicCapIcon, permission: 'lms.view' },
  { name: 'Documenti', href: '/documents', icon: DocumentTextIcon, permission: 'documents.view' },
  { name: 'Eventi', href: '/events', icon: CalendarIcon, permission: 'events.view' },
  { name: 'Task Force', href: '/taskforce', icon: UserGroupIcon, permission: 'taskforce.view' },
  // Compliance Clinic con sottomenu Quesiti e Servizi
  {
    name: 'Compliance Clinic',
    icon: QuestionMarkCircleIcon,
    permission: 'services.view',
    children: [
      { name: 'Quesiti', href: '/clinic', icon: QuestionMarkCircleIcon },
      { name: 'Servizi', href: '/services', icon: CubeIcon },
    ]
  },
  // Gaming con sottomenu Tornei e Classifica
  {
    name: 'Gaming',
    icon: TrophyIcon,
    permission: 'gamification.view',
    children: [
      { name: 'Tornei', href: '/tournaments', icon: TrophyIcon },
      { name: 'Classifica', href: '/leaderboard', icon: StarIcon },
    ]
  },
]

// Navigazione admin - ogni voce ha UN permesso ESATTO dal database
// Se l'admin cambia i permessi nel pannello, il menu si aggiorna al prossimo login
const adminNavigation: NavItem[] = [
  { name: 'Admin Dashboard', href: '/gestione', icon: ChartBarIcon, permission: 'admin.access_panel', exactMatch: true },
  { name: 'Gestione Utenti', href: '/gestione/users', icon: UserGroupIcon, permission: 'admin.manage_users' },
  { name: 'Ruoli e Permessi', href: '/gestione/permissions', icon: ShieldCheckIcon, permission: 'admin.manage_roles' },
  { name: 'Gestione Forum', href: '/gestione/forum', icon: ChatBubbleLeftRightIcon, permission: 'forum.moderate' },
  { name: 'Gestione Corsi', href: '/gestione/courses', icon: AcademicCapIcon, permission: 'lms.create_course' },
  { name: 'Modelli Attestato', href: '/gestione/attestati', icon: CheckBadgeIcon, permission: 'lms.edit_course' },
  { name: 'Gestione Eventi', href: '/gestione/events', icon: CalendarIcon, permission: 'events.create' },
  { name: 'Gestione Documenti', href: '/gestione/documents', icon: DocumentTextIcon, permission: 'documents.approve' },
  { name: 'Gestione Task Force', href: '/gestione/taskforce', icon: UserGroupIcon, permission: 'taskforce.manage_members' },
  // Gestione Clinic con sottomenu Quesiti, Smistamento e Servizi
  {
    name: 'Gestione Clinic',
    icon: QuestionMarkCircleIcon,
    permission: 'admin.access_panel',
    children: [
      { name: 'Gestione Quesiti', href: '/gestione/clinic', icon: QuestionMarkCircleIcon },
      { name: 'Smistamento Auto', href: '/gestione/clinic-routing', icon: BoltIcon },
      { name: 'Gestione Servizi', href: '/gestione/services', icon: CubeIcon },
    ]
  },
  { name: 'Gestione Gamification', href: '/gestione/gamification', icon: StarIcon, permission: 'gamification.manage_badges' },
  { name: 'Gestione Tornei', href: '/gestione/tornei', icon: TrophyIcon, permission: 'admin.access_panel' },
  { name: 'Gestione Newsletter', href: '/gestione/newsletter', icon: EnvelopeIcon, permission: 'newsletter.create' },
]

// Voci solo per super_admin (impostazioni di sistema)
const superAdminNavigation: NavItem[] = [
  { name: 'Impostazioni Sistema', href: '/gestione/settings', icon: Cog6ToothIcon, superAdminOnly: true },
]

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { theme } = useTheme()
  const { hasPermission, isSuperAdmin } = usePermissions()

  // Toggle menu collapsibile
  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? prev.filter(m => m !== menuName)
        : [...prev, menuName]
    )
  }

  // Verifica se un path è attivo (supporta exactMatch)
  const isPathActive = (item: NavItem) => {
    if (!item.href) return false
    if (item.exactMatch) {
      return location.pathname === item.href
    }
    return location.pathname.startsWith(item.href)
  }

  // Verifica se un child è attivo (match esatto)
  const isChildActive = (children?: NavItem[]) => {
    if (!children) return false
    return children.some(child => child.href && location.pathname === child.href)
  }

  // Espande automaticamente i menu con child attivi al cambio di pathname
  useEffect(() => {
    const menusToExpand: string[] = []

    // Controlla navigation principale
    navigation.forEach(item => {
      if (item.children && isChildActive(item.children)) {
        menusToExpand.push(item.name)
      }
    })

    // Controlla navigation admin
    adminNavigation.forEach(item => {
      if (item.children && isChildActive(item.children)) {
        menusToExpand.push(item.name)
      }
    })

    if (menusToExpand.length > 0) {
      setExpandedMenus(prev => {
        const newExpanded = [...prev]
        menusToExpand.forEach(name => {
          if (!newExpanded.includes(name)) {
            newExpanded.push(name)
          }
        })
        return newExpanded
      })
    }
  }, [location.pathname])

  // Fetch unread notifications count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const data = await dashboardApi.getNotifications({ page: 1 })
        const unread = data.results.filter((n: { is_read: boolean }) => !n.is_read).length
        setUnreadNotifications(unread)
      } catch (error) {
        console.error('Error fetching notifications:', error)
      }
    }

    fetchUnreadCount()
    // Refresh every 60 seconds
    const interval = setInterval(fetchUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Filtra navigazione principale in base ai permessi SINGOLI
  const filteredNavigation = useMemo(() => {
    return navigation.filter((item) => {
      // Dashboard sempre visibile
      if (!item.permission) return true
      // Verifica il permesso ESATTO
      return hasPermission(item.permission)
    })
  }, [hasPermission])

  // Filtra le voci di navigazione admin in base ai permessi SINGOLI dell'utente
  const filteredAdminNavigation = useMemo(() => {
    return adminNavigation.filter((item) => {
      // Se non ha permesso specificato, sempre visibile
      if (!item.permission) return true
      // Verifica il permesso ESATTO dal database
      return hasPermission(item.permission)
    })
  }, [hasPermission])

  // Filtra le voci super admin
  const filteredSuperAdminNavigation = useMemo(() => {
    return superAdminNavigation.filter((item) => {
      if (item.superAdminOnly) {
        return isSuperAdmin
      }
      return true
    })
  }, [isSuperAdmin])

  // L'utente ha accesso al menu admin se ha almeno una voce visibile
  const hasAdminAccess = filteredAdminNavigation.length > 0

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile sidebar */}
      <div
        className={clsx(
          'fixed inset-0 z-50 lg:hidden',
          sidebarOpen ? 'block' : 'hidden'
        )}
      >
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4 border-b">
            <div className="flex items-center gap-2">
              <svg width="28" height="28" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="30" fill="#0f3460" fillOpacity="0.1"/>
                <path d="M32 12L32 42" stroke="#0f3460" strokeWidth="4" strokeLinecap="round"/>
                <path d="M22 24L32 12L42 24" stroke="#0f3460" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 52C20 47.5817 24.4772 44 30 44H34C39.5228 44 44 47.5817 44 52" stroke="#0f3460" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="32" cy="36" r="6" fill="#0f3460"/>
              </svg>
              <span className="text-lg font-bold" style={{ fontFamily: "'Varela Round', sans-serif" }}>
                <span className="text-blue-900">CONQUIST</span>{' '}
                <span className="text-gray-800">GrowUp!</span>
              </span>
            </div>
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            {filteredNavigation.map((item) => (
              item.children ? (
                // Menu collapsibile con children
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={clsx(
                      'flex items-center justify-between w-full px-4 py-2 text-sm font-medium',
                      isChildActive(item.children)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                    <ChevronDownIcon
                      className={clsx(
                        'h-4 w-4 transition-transform',
                        expandedMenus.includes(item.name) ? 'rotate-180' : ''
                      )}
                    />
                  </button>
                  {expandedMenus.includes(item.name) && (
                    <div className="ml-4 border-l border-gray-200">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          to={child.href!}
                          className={clsx(
                            'flex items-center px-4 py-2 text-sm font-medium',
                            location.pathname === child.href
                              ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                              : 'text-gray-600 hover:bg-gray-50'
                          )}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <child.icon className="h-4 w-4 mr-3" />
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Menu normale (link diretto)
                <Link
                  key={item.name}
                  to={item.href!}
                  className={clsx(
                    'flex items-center px-4 py-2 text-sm font-medium',
                    isPathActive(item)
                      ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              )
            ))}
            {hasAdminAccess && (
              <>
                <div className="my-4 border-t" />
                <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Amministrazione</p>
                {filteredAdminNavigation.map((item) => (
                  item.children ? (
                    // Menu collapsibile con children
                    <div key={item.name}>
                      <button
                        onClick={() => toggleMenu(item.name)}
                        className={clsx(
                          'flex items-center justify-between w-full px-4 py-2 text-sm font-medium',
                          isChildActive(item.children)
                            ? 'bg-primary-50 text-primary-600'
                            : 'text-gray-600 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex items-center">
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.name}
                        </div>
                        <ChevronDownIcon
                          className={clsx(
                            'h-4 w-4 transition-transform',
                            expandedMenus.includes(item.name) ? 'rotate-180' : ''
                          )}
                        />
                      </button>
                      {expandedMenus.includes(item.name) && (
                        <div className="ml-4 border-l border-gray-200">
                          {item.children.map(child => (
                            <Link
                              key={child.href}
                              to={child.href!}
                              className={clsx(
                                'flex items-center px-4 py-2 text-sm font-medium',
                                location.pathname === child.href
                                  ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                                  : 'text-gray-600 hover:bg-gray-50'
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <child.icon className="h-4 w-4 mr-3" />
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Menu normale (link diretto)
                    <Link
                      key={item.name}
                      to={item.href!}
                      className={clsx(
                        'flex items-center px-4 py-2 text-sm font-medium',
                        isPathActive(item)
                          ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  )
                ))}
                {filteredSuperAdminNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={clsx(
                      'flex items-center px-4 py-2 text-sm font-medium',
                      isPathActive(item)
                        ? 'bg-primary-50 text-primary-600 border-r-2 border-primary-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </Link>
                ))}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col h-screen" style={{ backgroundColor: theme.colors.sidebarBg }}>
          <div className="flex h-16 items-center px-6 border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2">
              {theme.logoUrl ? (
                <img src={theme.logoUrl} alt={theme.companyName} className="h-8 w-auto" />
              ) : (
                <svg width="28" height="28" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="30" fill={theme.colors.sidebarText} fillOpacity="0.15"/>
                  <path d="M32 12L32 42" stroke={theme.colors.sidebarText} strokeWidth="4" strokeLinecap="round"/>
                  <path d="M22 24L32 12L42 24" stroke={theme.colors.sidebarText} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 52C20 47.5817 24.4772 44 30 44H34C39.5228 44 44 47.5817 44 52" stroke={theme.colors.sidebarText} strokeWidth="3" strokeLinecap="round"/>
                  <circle cx="32" cy="36" r="6" fill={theme.colors.sidebarText}/>
                </svg>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-bold leading-tight" style={{ fontFamily: "'Varela Round', sans-serif" }}>
                  <span style={{ color: theme.colors.companyColorSidebar }}>{theme.companyName}</span>{' '}
                  <span style={{ color: theme.colors.sidebarText }}>{theme.appName}</span>
                </span>
                <span className="text-xs" style={{ color: theme.colors.sidebarText, opacity: 0.7 }}>{theme.tagline}</span>
              </div>
            </div>
          </div>
          <nav className="sidebar-nav flex-1 overflow-y-auto py-4">
            {filteredNavigation.map((item) => (
              item.children ? (
                // Menu collapsibile con children
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={clsx(
                      'flex items-center justify-between w-full px-6 py-2.5 text-sm font-medium transition-colors',
                      isChildActive(item.children)
                        ? 'bg-slate-500/50 text-white'
                        : 'text-slate-200 hover:bg-slate-500/30 hover:text-white'
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </div>
                    <ChevronDownIcon
                      className={clsx(
                        'h-4 w-4 transition-transform',
                        expandedMenus.includes(item.name) ? 'rotate-180' : ''
                      )}
                    />
                  </button>
                  {expandedMenus.includes(item.name) && (
                    <div className="ml-4 border-l border-slate-500">
                      {item.children.map(child => (
                        <Link
                          key={child.href}
                          to={child.href!}
                          className={clsx(
                            'flex items-center px-6 py-2 text-sm font-medium transition-colors',
                            location.pathname === child.href
                              ? 'bg-slate-500/50 text-white border-r-2 border-primary-500'
                              : 'text-slate-300 hover:bg-slate-500/30 hover:text-white'
                          )}
                        >
                          <child.icon className="h-4 w-4 mr-3" />
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                // Menu normale (link diretto)
                <Link
                  key={item.name}
                  to={item.href!}
                  className={clsx(
                    'flex items-center px-6 py-2.5 text-sm font-medium transition-colors',
                    isPathActive(item)
                      ? 'bg-slate-500/50 text-white border-r-2 border-primary-500'
                      : 'text-slate-200 hover:bg-slate-500/30 hover:text-white'
                  )}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              )
            ))}
            {hasAdminAccess && (
              <>
                <div className="my-4 mx-4 border-t border-slate-500" />
                <p className="px-6 py-2 text-xs font-semibold text-slate-400 uppercase">Amministrazione</p>
                {filteredAdminNavigation.map((item) => (
                  item.children ? (
                    // Menu collapsibile con children
                    <div key={item.name}>
                      <button
                        onClick={() => toggleMenu(item.name)}
                        className={clsx(
                          'flex items-center justify-between w-full px-6 py-2.5 text-sm font-medium transition-colors',
                          isChildActive(item.children)
                            ? 'bg-slate-500/50 text-white'
                            : 'text-slate-200 hover:bg-slate-500/30 hover:text-white'
                        )}
                      >
                        <div className="flex items-center">
                          <item.icon className="h-5 w-5 mr-3" />
                          {item.name}
                        </div>
                        <ChevronDownIcon
                          className={clsx(
                            'h-4 w-4 transition-transform',
                            expandedMenus.includes(item.name) ? 'rotate-180' : ''
                          )}
                        />
                      </button>
                      {expandedMenus.includes(item.name) && (
                        <div className="ml-4 border-l border-slate-500">
                          {item.children.map(child => (
                            <Link
                              key={child.href}
                              to={child.href!}
                              className={clsx(
                                'flex items-center px-6 py-2 text-sm font-medium transition-colors',
                                location.pathname === child.href
                                  ? 'bg-slate-500/50 text-white border-r-2 border-primary-500'
                                  : 'text-slate-300 hover:bg-slate-500/30 hover:text-white'
                              )}
                            >
                              <child.icon className="h-4 w-4 mr-3" />
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Menu normale (link diretto)
                    <Link
                      key={item.name}
                      to={item.href!}
                      className={clsx(
                        'flex items-center px-6 py-2.5 text-sm font-medium transition-colors',
                        isPathActive(item)
                          ? 'bg-slate-500/50 text-white border-r-2 border-primary-500'
                          : 'text-slate-200 hover:bg-slate-500/30 hover:text-white'
                      )}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  )
                ))}
                {filteredSuperAdminNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href!}
                    className={clsx(
                      'flex items-center px-6 py-2.5 text-sm font-medium transition-colors',
                      isPathActive(item)
                        ? 'bg-slate-500/50 text-white border-r-2 border-primary-500'
                        : 'text-slate-200 hover:bg-slate-500/30 hover:text-white'
                    )}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </Link>
                ))}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header
          className="sticky top-0 z-40 backdrop-blur-sm shadow-sm"
          style={{ backgroundColor: `${theme.colors.sidebarBg}f2` }}
        >
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              style={{ color: theme.colors.sidebarText }}
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Notifications */}
              <Link
                to="/notifications"
                className="relative p-2 transition-colors hover:opacity-80"
                style={{ color: theme.colors.sidebarText }}
              >
                <BellIcon className="h-6 w-6" />
                {unreadNotifications > 0 && (
                  <span
                    className="absolute top-1 right-1 h-2 w-2 rounded-full"
                    style={{ backgroundColor: theme.colors.primary }}
                  />
                )}
              </Link>

              {/* User menu */}
              <div className="flex items-center gap-3">
                <Link to="/profile" className="flex items-center gap-2 hover:opacity-80">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="h-8 w-8 rounded-full ring-2 ring-white/40" />
                  ) : (
                    <UserCircleIcon className="h-8 w-8" style={{ color: theme.colors.sidebarText, opacity: 0.8 }} />
                  )}
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium" style={{ color: theme.colors.sidebarText }}>
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs capitalize" style={{ color: theme.colors.sidebarText, opacity: 0.7 }}>{user?.role?.replace('_', ' ')}</p>
                  </div>
                </Link>

                <button
                  onClick={handleLogout}
                  className="p-2 transition-colors hover:opacity-80"
                  style={{ color: theme.colors.sidebarText }}
                  title="Esci"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: theme.colors.pageBg, minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </main>

        {/* Floating Help Button */}
        <HelpButton />
      </div>
    </div>
  )
}
